import { type Component, onMount, onCleanup } from "solid-js";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { audioLevel, phase, chunkProgress } from "../lib/store.ts";
import type { AudioPipeline } from "../lib/audio-pipeline.ts";

// ── Config ────────────────────────────────────────────────────────────────
const RING_PTS = 200;
const RING_R = 3.6;
const BAR_COUNT = 80;
const BAR_INNER = 4.4;
const BAR_MAX_H = 2.8;
const PARTICLE_N = 350;

// ── Colour palette ────────────────────────────────────────────────────────
function gradColour(t: number): [number, number, number] {
  t = Math.max(0, Math.min(1, t));
  const stops: [number, [number, number, number]][] = [
    [0, [0, 240, 255]],
    [0.17, [0, 140, 255]],
    [0.33, [110, 0, 255]],
    [0.5, [180, 74, 255]],
    [0.67, [255, 45, 85]],
    [0.83, [255, 140, 0]],
    [1, [255, 230, 0]],
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, c0] = stops[i]!;
    const [t1, c1] = stops[i + 1]!;
    if (t >= t0 && t <= t1) {
      const f = (t - t0) / (t1 - t0);
      return [
        (c0[0] + (c1[0] - c0[0]) * f) / 255,
        (c0[1] + (c1[1] - c0[1]) * f) / 255,
        (c0[2] + (c1[2] - c0[2]) * f) / 255,
      ];
    }
  }
  const last = stops[stops.length - 1]![1];
  return [last[0] / 255, last[1] / 255, last[2] / 255];
}

export interface VisualizerProps {
  /** AudioPipeline (provides analyser for time-domain ring animation). */
  pipeline: AudioPipeline | null;
}

const Visualizer: Component<VisualizerProps> = (props) => {
  let threeCanvas: HTMLCanvasElement | undefined;
  let overlayCanvas: HTMLCanvasElement | undefined;
  let animId = 0;

  onMount(() => {
    if (!threeCanvas || !overlayCanvas) return;

    // ── Three.js scene ──────────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(
      60, innerWidth / innerHeight, 0.1, 100,
    );
    camera.position.set(0, 0, 12);

    const renderer = new THREE.WebGLRenderer({
      canvas: threeCanvas, antialias: true,
    });
    renderer.setSize(innerWidth, innerHeight);
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.8;

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(innerWidth, innerHeight), 1.2, 0.45, 0.82,
    );
    composer.addPass(bloom);

    // ── Ring ────────────────────────────────────────────────────────────
    const ringPos = new Float32Array((RING_PTS + 1) * 3);
    const ringCol = new Float32Array((RING_PTS + 1) * 3);
    for (let i = 0; i <= RING_PTS; i++) {
      const a = ((i % RING_PTS) / RING_PTS) * Math.PI * 2;
      ringPos[i * 3] = Math.cos(a) * RING_R;
      ringPos[i * 3 + 1] = Math.sin(a) * RING_R;
      ringPos[i * 3 + 2] = 0;
      const c = gradColour((i % RING_PTS) / RING_PTS);
      ringCol[i * 3] = c[0]; ringCol[i * 3 + 1] = c[1]; ringCol[i * 3 + 2] = c[2];
    }
    const ringGeo = new THREE.BufferGeometry();
    ringGeo.setAttribute("position", new THREE.BufferAttribute(ringPos, 3));
    ringGeo.setAttribute("color", new THREE.BufferAttribute(ringCol, 3));
    const ringMat = new THREE.LineBasicMaterial({
      vertexColors: true, transparent: true, opacity: 0.45,
      blending: THREE.AdditiveBlending,
    });
    const ring = new THREE.LineLoop(ringGeo, ringMat);
    scene.add(ring);

    // ── Frequency bars ──────────────────────────────────────────────────
    const barGeo = new THREE.BoxGeometry(0.035, 1, 0.035);
    const bars: { mesh: THREE.Mesh; angle: number }[] = [];
    for (let i = 0; i < BAR_COUNT; i++) {
      const a = (i / BAR_COUNT) * Math.PI * 2;
      const col = new THREE.Color().fromArray(gradColour(i / BAR_COUNT));
      const mat = new THREE.MeshBasicMaterial({
        color: col, transparent: true, opacity: 0.4,
        blending: THREE.AdditiveBlending,
      });
      const mesh = new THREE.Mesh(barGeo, mat);
      mesh.position.set(Math.cos(a) * BAR_INNER, Math.sin(a) * BAR_INNER, 0);
      mesh.rotation.z = a - Math.PI / 2;
      scene.add(mesh);
      bars.push({ mesh, angle: a });
    }

    // ── Particles ───────────────────────────────────────────────────────
    const pGeo = new THREE.BufferGeometry();
    const pPos = new Float32Array(PARTICLE_N * 3);
    const pVel = new Float32Array(PARTICLE_N * 3);
    for (let i = 0; i < PARTICLE_N; i++) {
      pPos[i * 3] = (Math.random() - 0.5) * 22;
      pPos[i * 3 + 1] = (Math.random() - 0.5) * 16;
      pPos[i * 3 + 2] = (Math.random() - 0.5) * 10 - 4;
      pVel[i * 3] = (Math.random() - 0.5) * 0.003;
      pVel[i * 3 + 1] = (Math.random() - 0.5) * 0.003;
      pVel[i * 3 + 2] = (Math.random() - 0.5) * 0.001;
    }
    pGeo.setAttribute("position", new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({
      color: 0x5533cc, size: 0.03, transparent: true, opacity: 0.3,
      blending: THREE.AdditiveBlending, sizeAttenuation: true,
    });
    scene.add(new THREE.Points(pGeo, pMat));

    // ── Inner decoration rings ──────────────────────────────────────────
    const innerRingGeo = new THREE.RingGeometry(3.0, 3.03, 128);
    const innerRingMat = new THREE.MeshBasicMaterial({
      color: 0x00f0ff, transparent: true, opacity: 0.05,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    const innerRing = new THREE.Mesh(innerRingGeo, innerRingMat);
    scene.add(innerRing);

    const outerRingGeo = new THREE.RingGeometry(8.0, 8.04, 128);
    const outerRingMat = new THREE.MeshBasicMaterial({
      color: 0xb44aff, transparent: true, opacity: 0.03,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    scene.add(new THREE.Mesh(outerRingGeo, outerRingMat));

    // ── 2D overlay ──────────────────────────────────────────────────────
    const ctx = overlayCanvas.getContext("2d")!;
    function resizeOverlay() {
      overlayCanvas!.width = innerWidth * devicePixelRatio;
      overlayCanvas!.height = innerHeight * devicePixelRatio;
      overlayCanvas!.style.width = innerWidth + "px";
      overlayCanvas!.style.height = innerHeight + "px";
      ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    }
    resizeOverlay();

    // ── Update functions ────────────────────────────────────────────────
    const ringPosAttr = ringGeo.getAttribute("position")!;
    const pPosAttr = pGeo.getAttribute("position")!;

    function updateRing(time: number) {
      const pipeline = props.pipeline;
      const analyser = pipeline?.analyserNode;
      const timeData = pipeline?.timeData;
      if (!analyser || !timeData) return;
      analyser.getByteTimeDomainData(timeData);
      const pos = ringPosAttr.array as Float32Array;
      const level = audioLevel();
      for (let i = 0; i < RING_PTS; i++) {
        const idx = Math.floor((i / RING_PTS) * timeData.length);
        const raw = (timeData[idx]! - 128) / 128;
        const amp = Math.abs(raw);
        const scale = 1 + amp * 0.45 + level * 0.06;
        const a = (i / RING_PTS) * Math.PI * 2;
        pos[i * 3] = Math.cos(a) * RING_R * scale;
        pos[i * 3 + 1] = Math.sin(a) * RING_R * scale;
        pos[i * 3 + 2] = Math.sin(a * 3 + time * 0.001) * amp * 0.15;
      }
      pos[RING_PTS * 3] = pos[0]!;
      pos[RING_PTS * 3 + 1] = pos[1]!;
      pos[RING_PTS * 3 + 2] = pos[2]!;
      ringPosAttr.needsUpdate = true;
      ringMat.opacity = 0.3 + level * 0.35;
    }

    function updateBars() {
      const pipeline = props.pipeline;
      const analyser = pipeline?.analyserNode;
      const freqData = pipeline?.freqData;
      if (!analyser || !freqData) return;
      analyser.getByteFrequencyData(freqData);
      const step = freqData.length / BAR_COUNT;
      for (let i = 0; i < BAR_COUNT; i++) {
        let sum = 0;
        const base = Math.floor(i * step);
        const n = Math.max(1, Math.floor(step));
        for (let j = 0; j < n && base + j < freqData.length; j++)
          sum += freqData[base + j]!;
        const val = sum / n / 255;
        const h = 0.04 + val * BAR_MAX_H;
        const m = bars[i]!.mesh;
        m.scale.set(1, h, 1);
        (m.material as THREE.MeshBasicMaterial).opacity = 0.15 + val * 0.5;
      }
    }

    function updateParticles() {
      const pos = pPosAttr.array as Float32Array;
      const level = audioLevel();
      for (let i = 0; i < PARTICLE_N; i++) {
        const x = i * 3, y = i * 3 + 1, z = i * 3 + 2;
        pos[x] = (pos[x] ?? 0) + (pVel[x] ?? 0) + Math.sin(Date.now() * 0.0003 + i) * 0.0006;
        pos[y] = (pos[y] ?? 0) + (pVel[y] ?? 0) + Math.cos(Date.now() * 0.0002 + i * 0.7) * 0.0006;
        pos[z] = (pos[z] ?? 0) + (pVel[z] ?? 0);
        if (Math.abs(pos[x] ?? 0) > 11) pos[x] *= -0.9;
        if (Math.abs(pos[y] ?? 0) > 8) pos[y] *= -0.9;
        if (Math.abs(pos[z] ?? 0) > 6) pos[z] *= -0.9;
      }
      pPosAttr.needsUpdate = true;
      pMat.opacity = 0.15 + level * 0.35;
      pMat.size = 0.025 + level * 0.025;
    }

    // Track inference start time for spinner animation
    let inferStartTime = 0;

    function drawOverlay(t: number) {
      const w = innerWidth, h = innerHeight;
      const cx = w / 2, cy = h / 2;
      const level = audioLevel();
      ctx.clearRect(0, 0, w, h);

      // Centre glow
      const glowR = 120 + level * 200;
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      grd.addColorStop(0, `rgba(0,240,255,${0.03 + level * 0.08})`);
      grd.addColorStop(0.4, `rgba(0,80,255,${0.01 + level * 0.03})`);
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grd;
      ctx.beginPath(); ctx.arc(cx, cy, glowR, 0, Math.PI * 2); ctx.fill();

      // Progress / spinner ring (when actively recording or inferring)
      const p = phase();
      if (p !== "startup" && p !== "ready") {
        const r = 110;
        const lw = 1.5;

        // Background ring track
        ctx.strokeStyle = "rgba(255,255,255,.03)";
        ctx.lineWidth = lw;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();

        if (p === "recording" || p === "silence") {
          // Recording: progress ring driven by chunk buffer progress (0..1)
          const pct = chunkProgress();
          ctx.strokeStyle = `rgba(0,240,255,${0.2 + level * 0.3})`;
          ctx.lineWidth = lw + level * 1.5;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + pct * Math.PI * 2);
          ctx.stroke();
        } else if (p === "inferring") {
          // Track when inference started for smooth spin animation
          if (inferStartTime === 0) inferStartTime = Date.now();
          // Inferring: spinning arc to show work in progress
          const spin = ((Date.now() - inferStartTime) / 1000) * Math.PI * 1.5;
          const arcLen = Math.PI * 0.6; // 108-degree arc
          ctx.strokeStyle = `rgba(180,74,255,${0.4 + level * 0.3})`;
          ctx.lineWidth = lw + 1;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.arc(cx, cy, r, spin, spin + arcLen);
          ctx.stroke();
        }

        // Reset inference start tracker when not inferring
        if (p !== "inferring") inferStartTime = 0;
      }

      // Vignette
      const vig = ctx.createRadialGradient(cx, cy, w * 0.25, cx, cy, w * 0.75);
      vig.addColorStop(0, "rgba(0,0,0,0)");
      vig.addColorStop(1, "rgba(0,0,0,.55)");
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, w, h);
    }

    // ── Animation loop ──────────────────────────────────────────────────
    function animate() {
      animId = requestAnimationFrame(animate);
      const t = performance.now();

      updateRing(t);
      updateBars();
      updateParticles();
      innerRing.material.opacity = 0.03 + audioLevel() * 0.06;
      innerRing.scale.setScalar(1 + Math.sin(t * 0.001) * 0.004 + audioLevel() * 0.015);

      drawOverlay(t);

      camera.position.x = Math.sin(t * 0.00012) * 0.15;
      camera.position.y = Math.cos(t * 0.00016) * 0.1;
      camera.lookAt(0, 0, 0);

      composer.render();
    }
    animate();

    // ── Resize ──────────────────────────────────────────────────────────
    function onResize() {
      camera.aspect = innerWidth / innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(innerWidth, innerHeight);
      composer.setSize(innerWidth, innerHeight);
      bloom.resolution.set(innerWidth, innerHeight);
      resizeOverlay();
    }
    window.addEventListener("resize", onResize);

    onCleanup(() => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
    });
  });

  return (
    <>
      <canvas ref={threeCanvas} id="three-canvas" />
      <canvas ref={overlayCanvas} id="overlay-canvas" />
    </>
  );
};

export default Visualizer;
